<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            <span>{{ title }}</span>
            <i v-if="hasSelectedPlan" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
          </h2>
          <div class='opc-zm-base-card__collapse opc-zm-base-card__collapse--reset'>
            <span>{{ $t('billing.hw.headset.config.desc') }}</span>
          </div>
        </div>
      </div>
      <div class="divide-line"></div>
      <div class="opc-addon-config__main">
        <zm-alert v-if="maxAlertOpts" class="opc-addon-config__main__alert" ref="maxAlert" v-bind="maxAlertOpts" />
        <headset-row
          v-for="dpItem in selectedPlans"
          :key="dpItem.plan_code"
          :value="dpItem"
          :showRemoveBtn="showRowRemove"
        ></headset-row>
        <div class="opc-zm-base-card__main__footer desk-phones-config__footer">
          <zm-button
            v-if="canAddNew"
            type="link"
            ghost
            @click="openZoomDeviceDialog(planType, $event)"
          >{{ $t('billing.hw.add_more_devices') }}</zm-button>
          <zm-button
            v-if="showFooterRemoveBtn"
            type="link"
            ghost
            @click="removePlanByKey"
            :aria-label="'remove ' + title + ' plan'"
          >{{ $t('billing.common.remove') }}</zm-button>
        </div>
      </div>
    </foldable>
  </tile>
</template>

<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import HeadsetRow from './components/headset-row.vue';

import CommonMixin from './mixin';
import plansMixin from '@/plans/mx/plan';
import { mapGetters, mapState } from "vuex";
import { planTypes } from '@/common/constants';
import { scrollToTop } from '@/views/checkout/utils'
import DeviceMixin from '@/plans/ZOOM_DEVICE/device-mx';
import { flowTypes, pageConfig } from '@/common/service/page-config';

export default {
  name: 'DeskPhonesConfig',
  mixins: [CommonMixin, plansMixin, DeviceMixin],
  components: {
    Foldable,
    HeadsetRow,
    Tile,
  },
  data() {
    return {
      maxAlertOpts: null,
      isExpanded: true,
      planType: planTypes.ZOOM_DEVICE$HEADSET,
    };
  },
  created() {
    this.$context.$on('headset-max-alert', (opts) => {
      opts && this.$tracking.buyInteraction('banner - product level', `headset - ${opts.title}. ${opts.description} - impression`);
      this.maxAlertOpts = opts
      if (opts) {
        !this.isExpanded && (this.isExpanded = true);
        this.$nextTick(() => {
          if (this.$refs.maxAlert) {
            scrollToTop(this.$refs.maxAlert.$el);
          }
        })
      }
    });
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
      allPlanMap: state => state.ShoppingCart.allPlanMap
    }),
    ...mapGetters(['getSelectedPlans']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === planTypes.ZOOM_DEVICE$HEADSET);
    },
    selectedPlans() {
      return this.getSelectedPlans.filter((p) => p.type === planTypes.ZOOM_DEVICE$HEADSET);
    },
    hasSelectedPlan() {
      return !!this.selectedPlans.length;
    },
    showFooterRemoveBtn() {
      const isBuyNowFlow = [flowTypes.buyNow, flowTypes.basic2pro].includes(pageConfig.type);
      return (
        (isBuyNowFlow || this.getSelectedPlans.filter(p => p.isVendorProduct).length > 1) && 
        !this.showRowRemove &&
        this.isShowRemoveBtn
      );
    },
    showRowRemove() {
      return this.selectedPlans.length > 1
    },
    canAddNew() {
      return !!this.mx_zoomDeviceDialogProducts.find(item => !item._disabled)
    },
    totalHeadset() {
      return this.selectedPlans.reduce((acc, item) => acc + item.cartInfo.selected_number, 0)
    }
  },
  watch: {
    totalHeadset(val) {
      if (val < 50) {
        this.maxAlertOpts = null
      }
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction('product details accordion', `headset - ${val ? 'expand' : 'collapse'}`);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@/styles/plans/addon-config.scss';
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

.opc-addon-config {
  &__title {
    font-weight: 500;
    font-size: 14px;
    color: #515079;

    i[check-icon] {
      font-size: 16px;
      color: #0B5CFF;
      margin-left: 12px;
    }

    &-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
    }
  }

  .opc-addon-config__subtitle {
    display: flex;
    align-items: center;

    ul {
      margin-left: 16px;
      display: flex;
      align-items: center;

      li span,
      li label {
        font-size: 14px;
        font-weight: 500;
        line-height: 120%;
        margin-left: 0;
        color: #00053D;
      }

      &>li:not(:first-child)::before {
        content: "•";
        margin: 0 10px;
      }
    }

    &-current {
      color: #6E7680;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      margin-right: 2px;
      text-transform: initial;
    }

    &-used {
      color: #131619;
      margin-left: 0;
      margin-right: 2px;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }

    &-overage {
      color: #B36200;
      margin: 0;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }
  }

  &__main {
    &__plan {
      display: flex;
      flex-wrap: wrap;
      margin-top: 24px;
      color: #00053D;

      .zm-radio {
        flex: 0 0 calc(50% - 8px);

        &__wrap {
          width: 100%;
        }

        &__label {
          &--inner {
            width: 100%;
          }

          &.is-disabled {

            .opc-addon-config__main__plan__price--amount,
            .opc-addon-config__main__plan__price--unit {
              color: #9191A4;
              @include darkTextDisable;
            }
          }
        }
      }

      &__capacity,
      &__price {
        line-height: 150%;
      }

      &__capacity {
        font-weight: 700;
        font-size: 16px;
        display: flex;
        align-items: center;

        &-recommend {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(96.59deg, #084845 10.36%, #09A639 89.64%);
          border-radius: 100px;
          padding: 0px 8px;
          color: #FFFFFF;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          margin-left: 8px;
        }
      }

      &__price {
        font-weight: 450;
        font-size: 22px;
        word-break: break-word;

        &--amount {
          font-size: 22px;
          font-weight: 700;
          color: #00053D;
          @include darkTextColor;
        }

        &--unit {
          font-size: 12px;
          font-weight: 500;
          color: #515079;
          @include darkTextColor2;
        }
      }
    }

    &__footer {
      margin-top: 24px;

      &--tips {
        font-weight: 500;
        font-size: 12px;
        color: #747487;
        text-align: left;
        line-height: 150%;
        @include darkTextColor2;
        @include darkTheme() {
          a {
            color: $dark-blue;
          }
        }
      }
    }
  }

  &__btn {
    &--wrap {
      margin-top: 24px;

      .zm-button {
        padding: 0px;
      }
    }
  }

  .opc-common-info-alert.zm-alert {
    background: #FEF7D9;
    padding: 16px;

    .zm-icon-warning {
      margin-right: 16px;

      &::before {
        content: "";
        background-image: url('../../assets/Alert.svg');
        width: 16px;
        height: 16px;
        background-repeat: no-repeat;
        background-size: cover;
        display: inline-block;
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
.opc-zm-base-card__main__footer.desk-phones-config__footer {
  display: flex;
  justify-content: space-between;
}
.opc-addon-config__main__alert.zm-alert {
  margin-bottom: 24px;
}
</style>
